import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Users2, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const nameOf = (fields, data) => {
  const keys = fields.filter((f) => f.label.toLowerCase().includes("name")).map((f) => f.key);
  return (keys.length ? keys : fields.slice(0, 1).map((f) => f.key)).map((k) => data[k] || "").join(" ").trim();
};

export const AttendancePanel = ({ value, onChange, locked }) => {
  const [census, setCensus] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/forms/templates/tpl_census/rows").then((r) => setCensus(r.data)).catch(() => setCensus({ rows: [], template: { fields: [] } })); }, []);
  const marks = useMemo(() => Object.fromEntries(value.map((a) => [a.row_id, a.status])), [value]);
  const members = useMemo(() => {
    if (!census) return [];
    const f = census.template.fields;
    const extra = f.find((x) => /rank|title|position/i.test(x.label));
    return census.rows.filter((r) => r.status === "active" || marks[r.row_id])
      .map((r) => ({ row_id: r.row_id, name: nameOf(f, r.data) || "Unnamed", sub: extra ? r.data[extra.key] : "" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [census, marks]);
  const set = (row_id, name, status) => onChange([...value.filter((a) => a.row_id !== row_id), ...(status ? [{ row_id, name, status }] : [])]);
  const markRest = (status) => onChange([...value, ...members.filter((m) => !marks[m.row_id]).map((m) => ({ row_id: m.row_id, name: m.name, status }))]);
  const present = value.filter((a) => a.status === "present").length;
  const absent = value.filter((a) => a.status === "absent").length;
  const s = q.trim().toLowerCase();
  const shown = members.filter((m) => !s || m.name.toLowerCase().includes(s));
  return (
    <div className="rounded-lg border border-amber-300/70 bg-white shadow-sm p-4 sm:p-5 fade-up" data-testid="attendance-panel">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Users2 className="w-4 h-4 text-amber-600" />
        <span className="font-semibold text-slate-900">Attendance</span>
        <span className="text-xs text-slate-500">· from the Frigate Census</span>
        <div className="ml-auto flex gap-2 text-xs font-semibold" data-testid="attendance-tally">
          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid="attendance-present-count">{present} present</span>
          <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-800" data-testid="attendance-absent-count">{absent} absent</span>
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid="attendance-unmarked-count">{Math.max(0, members.length - present - absent)} unmarked</span>
        </div>
      </div>
      {census && members.length === 0 ? (
        <p className="text-sm text-slate-600" data-testid="attendance-empty">No census members yet. <Link to="/census" className="underline text-amber-700">Import the Frigate Census</Link> first.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a member…" className="flex-1 min-w-[160px] bg-white h-10" data-testid="attendance-search" />
            {!locked && <Button variant="outline" size="sm" className="h-10" onClick={() => markRest("present")} data-testid="attendance-mark-rest-present">Unmarked → Present</Button>}
            {!locked && <Button variant="outline" size="sm" className="h-10" onClick={() => markRest("absent")} data-testid="attendance-mark-rest-absent">Unmarked → Absent</Button>}
          </div>
          <div className="max-h-[55vh] overflow-y-auto divide-y divide-slate-100 rounded-md border border-slate-100">
            {shown.map((m) => {
              const st = marks[m.row_id];
              return (
                <div key={m.row_id} className={`flex items-center gap-2 px-3 py-2 ${st === "present" ? "bg-emerald-50/70" : st === "absent" ? "bg-rose-50/70" : ""}`} data-testid={`attendance-row-${m.row_id}`}>
                  <div className="min-w-0 mr-auto"><div className="text-sm font-medium text-slate-900 truncate">{m.name}</div>{m.sub && <div className="text-xs text-slate-500 truncate">{m.sub}</div>}</div>
                  <Button size="sm" disabled={locked} variant={st === "present" ? "default" : "outline"} onClick={() => set(m.row_id, m.name, st === "present" ? null : "present")}
                    className={`h-10 min-w-[92px] ${st === "present" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`} data-testid={`attendance-present-${m.row_id}`}><Check className="w-4 h-4 mr-1" />Present</Button>
                  <Button size="sm" disabled={locked} variant={st === "absent" ? "default" : "outline"} onClick={() => set(m.row_id, m.name, st === "absent" ? null : "absent")}
                    className={`h-10 min-w-[88px] ${st === "absent" ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}`} data-testid={`attendance-absent-${m.row_id}`}><X className="w-4 h-4 mr-1" />Absent</Button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
